import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { EmailService } from '@core/services/email.service';
import { FormUtils } from '@core/utils/form-utils';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { Subscription } from 'rxjs';
import { IUserEventRegistrationDto, IUserRegistrationDto, UserLogged } from '../../interfaces/login.interfaces';
import { LoginService } from '../../services/login.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [TranslateModule, FormsModule, RouterModule, NgxIntlTelInputModule],
})
@UntilDestroy()
export class SignUpComponent implements OnInit {
  @Input() nextPageRoute: string = 'account';
  @Input() idEvent: number | null = null;
  @Output('changePage') nextPageEvent = new EventEmitter<{}>();

  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.Italy,
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  private subscription: Subscription;

  userCredentials: IUserRegistrationDto | IUserEventRegistrationDto = {
    languageCode: 'it',
    username: '',
    password: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    idCountry: 105,
    activeWithSMS: false,
    idEvent: this.idEvent,
    signUpPath: '/home',
    isAutonomousSeller: false,
  };
  userRegistration: UserLogged = new UserLogged(
    -1,
    '',
    '',
    '',
    -1,
    false,
    true,
    true,
    '',
    '',
    -1,
    ''
  );
  acceptPolicy = true;
  isProcessing = false;

  step = 0;
  isFirstnameEmpty = false;
  isLastnameEmpty = false;
  isPhoneEmpty = false;
  policyNotAccepted = false;
  isUsernameEmpty = false;
  isPasswordEmpty = false;
  isEmailInvalid = false;

  constructor(
    private loginService: LoginService,
    private emailService: EmailService,
    private languageService: LanguageService,
    public publicGenericService: PublicGenericService,
    public activeModal: NgbActiveModal,
    private recaptchaV3Service: ReCaptchaV3Service,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.emailService.showReCaptchaBadge();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.emailService.hideReCaptchaBadge();
  }

  changePage() {
    this.nextPageEvent.emit({});
  }

  changeStep(step: number) {
    this.userCredentials.email = this.userCredentials?.email
      ?.trim()
      ?.toLowerCase();
    this.userCredentials.phone =
      this.userCredentials.phoneData?.internationalNumber || '';

    if (step === 1) {
      if (this.userCredentials.firstName == '') {
        this.isFirstnameEmpty = true;
      } else {
        this.isFirstnameEmpty = false;
      }
      if (this.userCredentials.lastName == '') {
        this.isLastnameEmpty = true;
      } else {
        this.isLastnameEmpty = false;
      }
      if (FormUtils.emailRegex.test(this.userCredentials.email)) {
        this.isEmailInvalid = false;
      } else {
        this.isEmailInvalid = true;
      }
      if (this.userCredentials.phone == '') {
        this.isPhoneEmpty = true;
      } else {
        this.isPhoneEmpty = false;
      }
      if (!this.acceptPolicy) {
        this.policyNotAccepted = true;
      } else {
        this.policyNotAccepted = false;
      }
    }
    if (
      !this.isFirstnameEmpty &&
      !this.isLastnameEmpty &&
      !this.isEmailInvalid &&
      !this.isPhoneEmpty &&
      this.acceptPolicy
    ) {
      this.step = step;
    }
  }

  register(form: NgForm) {
    this.isProcessing = true;
    this.userCredentials.email = this.userCredentials?.email
      ?.trim()
      ?.toLowerCase();
    this.userCredentials.signUpPath = window.location.pathname;

    if (this.userCredentials.username == '') {
      this.isUsernameEmpty = true;
    } else {
      this.isUsernameEmpty = false;
    }
    if (this.userCredentials.password == '') {
      this.isPasswordEmpty = true;
    } else {
      this.isPasswordEmpty = false;
    }
    if (!this.isUsernameEmpty && !this.isPasswordEmpty) {
      this.userCredentials.languageCode =
        this.languageService.userSelectedLanguage.code;

      if (form.invalid) {
        for (const control of Object.keys(form.controls)) {
          form.controls[control].markAsTouched();
        }
        return;
      }

      this.subscription = this.recaptchaV3Service
        .execute('registration')
        .subscribe((token: string) => {
          this.emailService
            .verifyReCaptcha({ responseToken: token })
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              // fatto perché il parametro di input viene valorizzato in un secondo momento e la valorizzazione di userCredentials prende sempre null
              if (this.idEvent != this.userCredentials.idEvent) {
                this.userCredentials.idEvent = this.idEvent;
              }

              if (!!this.userCredentials.idEvent) {
                this.userCredentials.activeWithSMS = true;
                this.loginService
                  .registerForEvent(
                    this.userCredentials as IUserRegistrationDto
                  )
                  .subscribe((dataR) => {
                    this.userRegistration = dataR;
                    this.changeStep(2);
                    this.analyticsService.trackSignUp('registered');
                  });
              } else {
                this.userCredentials.activeWithSMS = false;
                this.loginService
                  .register(this.userCredentials as IUserRegistrationDto)
                  .subscribe((dataR) => {
                    this.userRegistration = dataR;
                    this.changeStep(2);
                    this.analyticsService.trackSignUp('registered');
                  });
              }

              this.isProcessing = false;
            });
        });
    } else {
      this.isProcessing = false;
    }
  }
}
